import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui";
import { Link } from "@tanstack/react-router";
import type {
  AssignmentGroup,
  AssignmentOption,
  CaseAssignmentsFilters,
  CaseAssignmentsResponse,
  ClosedAssignmentMatter,
} from "@/lib/data/requests/reports/reports-case-assignments";
import {
  ReportTable as AssignmentTable,
  count,
  decimal,
  moneyWithCents,
  type ReportColumn as AssignmentColumn,
} from "../shared/ReportTable";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { functionalUpdate } from "@tanstack/react-table";

type Props = {
  filters: CaseAssignmentsFilters;
  report?: CaseAssignmentsResponse;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  onFilters: (filters: CaseAssignmentsFilters) => void;
  onRefresh: () => void;
  onReset: () => void;
};

export function CaseAssignmentsReport({
  filters,
  report,
  isLoading,
  isFetching,
  isError,
  onFilters,
  onRefresh,
  onReset,
}: Props) {
  const data = report?.data;
  const update = (key: keyof CaseAssignmentsFilters, value: string) => {
    onFilters({ ...filters, [key]: value || undefined, pageNumber: 1 });
  };
  const team: AssignmentColumn<AssignmentGroup> = {
    label: "Team",
    value: (row) => row.label,
    render: (row) =>
      row.teamId ? (
        <button
          className="text-left hover:underline"
          onClick={() => {
            if (row.teamId) update("teamId", row.teamId);
          }}
        >
          {row.label}
        </button>
      ) : (
        row.label
      ),
  };
  const numeric = (
    label: string,
    key: keyof AssignmentGroup,
  ): AssignmentColumn<AssignmentGroup> => ({
    label,
    numeric: true,
    value: (row) => row[key],
    render: (row) => count(row[key] ?? null),
  });
  const decimalValue = (
    label: string,
    key: "averageOpenDays" | "averageDaysToClose" | "averageDaysToFirstDemand",
  ): AssignmentColumn<AssignmentGroup> => ({
    label,
    numeric: true,
    value: (row) => (row[key] === null ? null : Number(row[key])),
    render: (row) => decimal(row[key]),
  });
  const currency = (
    label: string,
    key:
      | "acceptedAmount"
      | "averageAcceptedAmount"
      | "expectedFees"
      | "trustDeposits",
  ): AssignmentColumn<AssignmentGroup> => ({
    label,
    numeric: true,
    value: (row) => (row[key] === null ? null : Number(row[key])),
    render: (row) => moneyWithCents(row[key]),
  });
  const current = [
    team,
    numeric("Current open", "openMatters"),
    numeric("Pre-litigation", "preLitigationMatters"),
    numeric("Litigation", "litigationMatters"),
    decimalValue("Avg open age (days)", "averageOpenDays"),
  ];
  const overview = [
    team,
    numeric("New matters", "newMatters"),
    numeric("Closed matters", "closedMatters"),
    decimalValue("Avg days to close", "averageDaysToClose"),
  ];
  const financial = [
    team,
    numeric("Demands sent", "demandsSent"),
    {
      label: "Demand conversion rate",
      numeric: true,
      value: (row: AssignmentGroup) =>
        row.demandConversionRate === null ||
        row.demandConversionRate === undefined
          ? null
          : Number(row.demandConversionRate),
      render: (row: AssignmentGroup) =>
        row.demandConversionRate == null
          ? "—"
          : `${decimal(row.demandConversionRate)}%`,
    },
    numeric("Accepted offers", "acceptedOffers"),
    currency("Accepted amount", "acceptedAmount"),
    currency("Avg accepted offer", "averageAcceptedAmount"),
    decimalValue("Avg days to first demand", "averageDaysToFirstDemand"),
    currency("Trust deposits", "trustDeposits"),
    currency("Expected fees", "expectedFees"),
  ];
  const closed: AssignmentColumn<ClosedAssignmentMatter>[] = [
    {
      label: "Matter",
      value: (row) => row.matterName ?? "",
      render: (row) => (
        <Link
          className="font-medium hover:underline"
          to="/matters/$matterId"
          params={{ matterId: row.matterId }}
        >
          {row.matterName || "Untitled matter"}
        </Link>
      ),
    },
    {
      label: "Team",
      value: (row) => row.teams.join(", "),
      render: (row) => row.teams.join(", ") || "Unassigned",
    },
    {
      label: "Closed",
      value: (row) => row.closedDate,
      render: (row) => row.closedDate,
    },
    {
      label: "Accepted amount",
      numeric: true,
      value: (row) =>
        row.acceptedAmount === null ? null : Number(row.acceptedAmount),
      render: (row) => moneyWithCents(row.acceptedAmount),
    },
    {
      label: "Expenses",
      numeric: true,
      value: (row) => Number(row.expenseCost),
      render: (row) => moneyWithCents(row.expenseCost),
    },
    {
      label: "Expected fees",
      numeric: true,
      value: (row) =>
        row.expectedFees === null ? null : Number(row.expectedFees),
      render: (row) => moneyWithCents(row.expectedFees),
    },
    {
      label: "Demands",
      numeric: true,
      value: (row) => row.demands,
      render: (row) => count(row.demands),
    },
    {
      label: "Demand conversion rate",
      numeric: true,
      value: (row) =>
        row.demandConversionRate === null ||
        row.demandConversionRate === undefined
          ? null
          : Number(row.demandConversionRate),
      render: (row) =>
        row.demandConversionRate == null
          ? "—"
          : `${decimal(row.demandConversionRate)}%`,
    },
    {
      label: "Accepted offers",
      numeric: true,
      value: (row) => row.acceptedOffers,
      render: (row) => count(row.acceptedOffers),
    },
    {
      label: "Days to close",
      numeric: true,
      value: (row) => row.daysToClose,
      render: (row) => count(row.daysToClose),
    },
  ];
  const select = (
    label: string,
    key: "teamId" | "roleId" | "userId" | "litigation",
    options: AssignmentOption[],
    all: string,
  ) => (
    <div className="min-w-36 flex-1 space-y-1.5">
      <label
        className="text-muted-foreground text-xs font-semibold tracking-wide uppercase"
        htmlFor={`assignments-${key}`}
      >
        {label}
      </label>
      <Select
        value={filters[key] || "all"}
        onValueChange={(value) => {
          update(key, value === "all" ? "" : value);
        }}
      >
        <SelectTrigger id={`assignments-${key}`}>
          <SelectValue placeholder={all} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{all}</SelectItem>
          {options.map((option) => (
            <SelectItem key={option.id} value={option.id}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
  const range = `${filters.startDate} – ${filters.endDate}`;
  const groupRows = data?.groups ?? [];
  const loading = isLoading;
  const section = (
    title: string,
    description: string,
    columns: AssignmentColumn<AssignmentGroup>[],
    tableName: (typeof tanstackTableNames)[keyof typeof tanstackTableNames],
    className = "",
  ) => (
    <section className={className} aria-description={description}>
      <div className="mb-4">
        <h2 className="text-2xl font-semibold tracking-tight">{title}</h2>
      </div>
      <div className="pb-2">
        <AssignmentTable
          columns={columns}
          rows={groupRows}
          rowKey={(row) => row.teamId ?? "unassigned"}
          total={data?.totals}
          isLoading={loading}
          tableName={tableName}
        />
      </div>
    </section>
  );
  return (
    <div className="space-y-6" aria-busy={isFetching}>
      <h1 className="text-3xl font-semibold tracking-tight">
        Case assignments
      </h1>
      <div className="flex flex-wrap items-end gap-3 pb-1">
        <div className="w-40 space-y-1.5">
          <label
            htmlFor="assignments-start"
            className="text-muted-foreground text-xs font-semibold tracking-wide uppercase"
          >
            Date start
          </label>
          <Input
            id="assignments-start"
            type="date"
            value={filters.startDate}
            max={filters.endDate}
            onChange={(event) => {
              if (event.target.value) update("startDate", event.target.value);
            }}
          />
        </div>
        <div className="w-40 space-y-1.5">
          <label
            htmlFor="assignments-end"
            className="text-muted-foreground text-xs font-semibold tracking-wide uppercase"
          >
            Date end
          </label>
          <Input
            id="assignments-end"
            type="date"
            value={filters.endDate}
            min={filters.startDate}
            onChange={(event) => {
              if (event.target.value) update("endDate", event.target.value);
            }}
          />
        </div>
        {select("Assigned user", "userId", data?.users ?? [], "All users")}
        {select(
          "Litigation",
          "litigation",
          [
            { id: "non-litigation", label: "Non-litigation" },
            { id: "litigation", label: "Litigation only" },
          ],
          "All litigation",
        )}
        {select("Teams", "teamId", data?.teams ?? [], "All teams")}
        <Button
          variant="outline"
          className="h-10 rounded-md px-3"
          onClick={onReset}
        >
          Reset filters
        </Button>
      </div>
      <span role="status" aria-live="polite" className="sr-only">
        {isFetching ? "Updating report" : "Report updated"}
      </span>
      {isError ? (
        <div role="alert" className="p-8 text-sm">
          The report could not be loaded.{" "}
          <button className="underline" onClick={onRefresh}>
            Try again
          </button>
        </div>
      ) : (
        <>
          {data &&
            (!data.freshness.ready ||
              data.freshness.pendingMatters > 0 ||
              data.freshness.missingMatters > 0) && (
              <div
                role="status"
                className="border-b bg-amber-50 px-5 py-3 text-sm text-amber-950"
              >
                Reporting is updating. {data.freshness.missingMatters} matters
                await their first projection and {data.freshness.pendingMatters}{" "}
                have pending changes. These figures may be incomplete.
              </div>
            )}
          <div className="grid gap-6 xl:grid-cols-2">
            {section(
              "Current open cases",
              "Current open matters by today's team assignments. Lead and opportunity categories are excluded.",
              current,
              tanstackTableNames.reportsCaseAssignmentsCurrent,
            )}
            {section(
              "Case overviews",
              `Matters created or currently closed in ${range}. Closing age runs from creation to the current closed status.`,
              overview,
              tanstackTableNames.reportsCaseAssignmentsOverview,
            )}
          </div>
          {section(
            "Offers, demands, and fees",
            `Activity dated ${range}, grouped by current teams. Expected fees use accepted offer amounts and their recorded fee percentages; they are not collected fees.`,
            financial,
            tanstackTableNames.reportsCaseAssignmentsFinancial,
          )}
          <section>
            <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="text-2xl font-semibold tracking-tight">
                  Closed cases
                </h2>
              </div>
              <div className="text-muted-foreground text-xs">
                {data?.totalClosedMatters ?? 0} matters · Page{" "}
                {filters.pageNumber}
              </div>
            </div>
            <div className="pb-2">
              <AssignmentTable
                columns={closed}
                rows={data?.closedMatters ?? []}
                rowKey={(row) => row.matterId}
                getMatterId={(row) => row.matterId}
                isLoading={loading}
                noResultsMessage="No closed matters match these filters."
                serverPagination={{
                  pagination: {
                    pageIndex: filters.pageNumber - 1,
                    pageSize: filters.pageSize,
                  },
                  onPaginationChange: (updater) => {
                    const pagination = functionalUpdate(updater, {
                      pageIndex: filters.pageNumber - 1,
                      pageSize: filters.pageSize,
                    });
                    onFilters({
                      ...filters,
                      pageNumber: pagination.pageIndex + 1,
                      pageSize: pagination.pageSize,
                    });
                  },
                  pageCount: Math.max(
                    1,
                    Math.ceil(
                      (data?.totalClosedMatters ?? 0) / filters.pageSize,
                    ),
                  ),
                  rowCount: data?.totalClosedMatters,
                  getCanNextPage: report?.pagination.hasMore,
                }}
                tableName={tanstackTableNames.reportsCaseAssignmentsClosed}
              />
            </div>
          </section>
        </>
      )}
    </div>
  );
}
